// Audit-trail PRESENTATION. Nothing here writes, and nothing here rewrites.
//
// The stored audit row is evidence: it is hash-chained, it is what was true
// when it was written, and it must never be mutated — see the writer note in
// backend/utils/logs.js and [[gotcha-audit-hash-timestamp-mismatch]]. So this
// module changes only what a reader SEES, and always keeps the stored string
// on screen beside it.
//
// Two problems it solves, both display-only:
//
//   The ACTION column showed the raw route — `GET /api/v1/claims/:id` — as the
//   primary human-readable label. That is an implementation detail, and on a
//   tenant that renames Claim it is also the wrong noun.
//
//   Rows written by the auditLogger middleware are route-shaped by nature;
//   rows written deliberately by a controller (`Assigned Case CL-2026-06982 to
//   …`) are already prose and are shown exactly as stored.
//
// Historical rows keep their original wording. A row stored years ago as
// "Viewed claims" still reads "Viewed claims" in the DETAIL column — the
// derived label sits in ACTION, where the raw route used to be, and the route
// itself moves to a secondary line. Nothing is lost; the order changes.

const ROUTE_RE = /^(GET|POST|PATCH|PUT|DELETE)\s+(\/\S*)$/i;

const VERB_KEY = {
  GET: 'audit.action.viewed',
  POST: 'audit.action.created',
  PATCH: 'audit.action.updated',
  PUT: 'audit.action.updated',
  DELETE: 'audit.action.deleted',
};

// Path segment → i18n key for the noun. The keys carry the tenant vocabulary
// variables, so a tenant that renames Claim sees its own word here without
// this file knowing a vocabulary system exists.
const RESOURCE_KEY = {
  claims: 'audit.resource.claims',
  policies: 'audit.resource.policies',
  clients: 'audit.resource.clients',
  brokers: 'audit.resource.brokers',
  'risk-carriers': 'audit.resource.riskCarriers',
  offers: 'audit.resource.offers',
  documents: 'audit.resource.documents',
  invoices: 'audit.resource.invoices',
  users: 'audit.resource.users',
  logs: 'audit.resource.logs',
};

/**
 * Pull the resource segment out of an API path.
 * `/api/v1/claims/:id/risk-signals` → `claims`
 */
function resourceFromPath(path) {
  const cleaned = String(path || '').split('?')[0];
  const segments = cleaned.split('/').filter(Boolean);
  const v = segments.indexOf('v1');
  const candidate = v >= 0 ? segments[v + 1] : segments[0];
  return candidate && RESOURCE_KEY[candidate] ? candidate : null;
}

/**
 * A readable label for an audit row's action.
 *
 * @param {Function} t       i18next t
 * @param {string}   action  the STORED action string
 * @returns {{ label: string, raw: string|null }}
 *          `label` is what to show; `raw` is the stored string to keep visible
 *          as evidence, or null when the stored string IS the label.
 */
export function auditActionLabel(t, action) {
  const stored = String(action ?? '').trim();
  if (!stored) return { label: '—', raw: null };

  const m = stored.match(ROUTE_RE);
  if (!m) {
    // Already prose — a controller wrote it deliberately. Show it verbatim.
    return { label: stored, raw: null };
  }

  const method = m[1].toUpperCase();
  const resource = resourceFromPath(m[2]);
  const verbKey = VERB_KEY[method];
  if (!verbKey || !resource) {
    // An unmapped method or an unrecognised resource. Rather than invent a
    // phrase, show the route — it is at least truthful.
    return { label: stored, raw: null };
  }

  return {
    label: t(verbKey, { resource: t(RESOURCE_KEY[resource]) }),
    raw: stored,
  };
}

export default auditActionLabel;
